import { useState } from "react";
import {
  BarChart3,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  Ticket,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownRight,
  Target,
} from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { KPICard, EmptyState } from "@/components/admin/shared";
import { usePromoAnalytics } from "@/hooks/admin/useMarketing";

// ---------------------------------------------------------------------------
// Period selector
// ---------------------------------------------------------------------------

type Period = "7d" | "30d" | "90d";

const PERIOD_LABELS: Record<Period, string> = {
  "7d": "7 derniers jours",
  "30d": "30 derniers jours",
  "90d": "90 derniers jours",
};

function PeriodSelector({
  value,
  onChange,
}: {
  readonly value: Period;
  readonly onChange: (p: Period) => void;
}) {
  return (
    <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1">
      {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === p
              ? "bg-amber-500 text-white"
              : "text-stone-600 hover:bg-stone-100"
          }`}
        >
          {PERIOD_LABELS[p]}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Metric card (enhanced KPI with trend)
// ---------------------------------------------------------------------------

interface MetricCardProps {
  readonly label: string;
  readonly value: string;
  readonly subLabel?: string;
  readonly icon?: React.ReactNode;
  readonly trend?: "up" | "down" | "neutral";
}

function MetricCard({ label, value, subLabel, icon, trend }: MetricCardProps) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">
          {label}
        </p>
        {icon && (
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-50 text-stone-400">
            {icon}
          </div>
        )}
      </div>
      <p className="font-display text-2xl font-semibold text-stone-900 tabular-nums">
        {value}
      </p>
      {subLabel && (
        <div className="flex items-center gap-1">
          {trend === "up" && (
            <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" />
          )}
          {trend === "down" && (
            <ArrowDownRight className="h-3.5 w-3.5 text-red-500" />
          )}
          <p className="text-xs text-stone-500">{subLabel}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison bar
// ---------------------------------------------------------------------------

function ComparisonBar({
  label,
  value,
  maxValue,
  color,
}: {
  readonly label: string;
  readonly value: number;
  readonly maxValue: number;
  readonly color: string;
}) {
  const pct = maxValue > 0 ? Math.min((value / maxValue) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-stone-600">{label}</span>
        <span className="tabular-nums font-semibold text-stone-800">
          {value.toLocaleString("fr-SN")} FCFA
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-stone-100">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function PromoAnalytics() {
  const [period, setPeriod] = useState<Period>("30d");
  const { data, isLoading, isError } = usePromoAnalytics(period);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <BarChart3 className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Analytique promotions
              </h1>
              <p className="text-sm text-stone-500">
                Impact des promotions sur les ventes
              </p>
            </div>
          </div>
          <PeriodSelector value={period} onChange={setPeriod} />
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-4 animate-pulse">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 rounded-xl bg-stone-100" />
              ))}
            </div>
            <div className="h-48 rounded-xl bg-stone-100" />
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Impossible de charger les données analytiques.
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && !data && (
          <EmptyState
            icon={<BarChart3 className="h-6 w-6" />}
            title="Aucune donnée disponible"
            description="Les statistiques apparaîtront ici une fois des promotions actives."
          />
        )}

        {/* Content */}
        {!isLoading && !isError && data && (
          <div className="space-y-6">
            {/* KPI row */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <MetricCard
                label="Remises accordées"
                value={`${(data.total_discount_given_fcfa ?? 0).toLocaleString("fr-SN")} FCFA`}
                icon={<TrendingDown className="h-4 w-4" />}
                subLabel={`${PERIOD_LABELS[period]}`}
              />
              <MetricCard
                label="Commandes avec promo"
                value={(data.orders_with_promo ?? 0).toLocaleString("fr-SN")}
                icon={<ShoppingCart className="h-4 w-4" />}
              />
              <MetricCard
                label="Remise moyenne"
                value={`${(data.avg_discount_percent ?? 0).toFixed(1)} %`}
                icon={<Target className="h-4 w-4" />}
              />
              <MetricCard
                label="Coupons utilises"
                value={(data.orders_with_promo ?? 0).toLocaleString("fr-SN")}
                icon={<Ticket className="h-4 w-4" />}
                subLabel="Sur la periode"
              />
            </div>

            {/* Promo vs No-promo */}
            <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-5">
              <h2 className="font-display text-base font-semibold text-stone-900">
                Panier moyen : avec promo vs sans promo
              </h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-5 text-center space-y-1">
                  <p className="text-xs font-medium text-stone-500">
                    Avec promotion
                  </p>
                  <p className="font-display text-2xl font-semibold text-emerald-700 tabular-nums">
                    {data.promo_vs_no_promo.with_promo_avg_order.toLocaleString(
                      "fr-SN",
                    )}{" "}
                    FCFA
                  </p>
                  <div className="flex items-center justify-center gap-1">
                    <ArrowUpRight className="h-3 w-3 text-emerald-600" />
                    <p className="text-xs text-emerald-600">
                      Panier plus eleve
                    </p>
                  </div>
                </div>
                <div className="rounded-lg bg-stone-50 border border-stone-100 p-5 text-center space-y-1">
                  <p className="text-xs font-medium text-stone-500">
                    Sans promotion
                  </p>
                  <p className="font-display text-2xl font-semibold text-stone-700 tabular-nums">
                    {data.promo_vs_no_promo.without_promo_avg_order.toLocaleString(
                      "fr-SN",
                    )}{" "}
                    FCFA
                  </p>
                  <p className="text-xs text-stone-400">Panier standard</p>
                </div>
              </div>

              {/* Comparison bars */}
              <div className="space-y-3 pt-2">
                <ComparisonBar
                  label="Avec promo"
                  value={data.promo_vs_no_promo.with_promo_avg_order}
                  maxValue={Math.max(
                    data.promo_vs_no_promo.with_promo_avg_order,
                    data.promo_vs_no_promo.without_promo_avg_order,
                  )}
                  color="bg-emerald-500"
                />
                <ComparisonBar
                  label="Sans promo"
                  value={data.promo_vs_no_promo.without_promo_avg_order}
                  maxValue={Math.max(
                    data.promo_vs_no_promo.with_promo_avg_order,
                    data.promo_vs_no_promo.without_promo_avg_order,
                  )}
                  color="bg-stone-400"
                />
              </div>
            </div>

            {/* Top promotions table */}
            {data.top_promotions.length > 0 && (
              <div className="rounded-xl border border-stone-200 bg-white">
                <div className="border-b border-stone-100 px-5 py-4 flex items-center justify-between">
                  <h2 className="font-display text-base font-semibold text-stone-900">
                    Top promotions
                  </h2>
                  <span className="text-xs text-stone-400">
                    Classement par remises accordees
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100 bg-stone-50">
                        <th className="w-12 px-5 py-3 text-left text-xs font-semibold text-stone-400">
                          #
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">
                          Promotion
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                          Utilisations
                        </th>
                        <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                          Remises accordées
                        </th>
                        <th className="hidden sm:table-cell px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-stone-500">
                          Remise / utilisation
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                      {data.top_promotions.map((tp, idx) => {
                        const avgPerUse =
                          tp.uses > 0
                            ? Math.round(tp.discount_fcfa / tp.uses)
                            : 0;

                        return (
                          <tr
                            key={idx}
                            className="hover:bg-stone-50 transition-colors"
                          >
                            <td className="px-5 py-3 text-stone-400 tabular-nums text-xs">
                              {idx + 1}
                            </td>
                            <td className="px-5 py-3 font-medium text-stone-900">
                              {tp.name}
                            </td>
                            <td className="px-5 py-3 text-right tabular-nums text-stone-600">
                              {(tp.uses ?? 0).toLocaleString("fr-SN")}
                            </td>
                            <td className="px-5 py-3 text-right tabular-nums font-semibold text-amber-700">
                              {(tp.discount_fcfa ?? 0).toLocaleString("fr-SN")} FCFA
                            </td>
                            <td className="hidden sm:table-cell px-5 py-3 text-right tabular-nums text-stone-500">
                              {avgPerUse.toLocaleString("fr-SN")} FCFA
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ROI insight card */}
            <div className="rounded-xl border border-stone-200 bg-white p-5 space-y-3">
              <h2 className="font-display text-base font-semibold text-stone-900">
                Retour sur investissement
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-stone-50 p-4 space-y-1">
                  <p className="text-xs text-stone-500">
                    Total remises accordees
                  </p>
                  <p className="font-display text-lg font-semibold text-red-600 tabular-nums">
                    -{data.total_discount_given_fcfa.toLocaleString("fr-SN")}{" "}
                    FCFA
                  </p>
                </div>
                <div className="rounded-lg bg-stone-50 p-4 space-y-1">
                  <p className="text-xs text-stone-500">
                    CA généré par les promos
                  </p>
                  <p className="font-display text-lg font-semibold text-emerald-700 tabular-nums">
                    {(
                      data.orders_with_promo *
                      data.promo_vs_no_promo.with_promo_avg_order
                    ).toLocaleString("fr-SN")}{" "}
                    FCFA
                  </p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 space-y-1">
                  <p className="text-xs text-stone-500">ROI estimatif</p>
                  <p className="font-display text-lg font-semibold text-amber-700 tabular-nums">
                    {data.total_discount_given_fcfa > 0
                      ? `${(
                          ((data.orders_with_promo *
                            data.promo_vs_no_promo.with_promo_avg_order) /
                            data.total_discount_given_fcfa) *
                          100
                        ).toFixed(0)} %`
                      : "\u2014"}
                  </p>
                  <p className="text-xs text-amber-600">
                    CA généré / remises accordees
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
